import {View, Text, StyleSheet, Platform} from 'react-native';
import React, {useState, useMemo} from 'react';
import {SkillzTextInput} from '@skillz/ui';
import {_m} from 'INTL';
import {WITHDRAWAL_INPUT_LABEL, WITHDRAWAL_INPUT_PLACEHOLDER} from './BankWithdrawalConstants';
import {default as SkillzStyle} from 'SkillzStyle';
import RTPBanner from './RTPBanner';

const {fontRegular} = SkillzStyle.fullStyles;

type WithdrawalInputProps = {
  withdrawLimit?: number;
  onChangeAmount?: (amount: string) => void;
  rtpEnabled?: boolean;
  rtpEligible?: boolean;
  rtpData?: any;
};

const WithdrawalInput = ({
  withdrawLimit = 0,
  onChangeAmount,
  rtpEnabled,
  rtpEligible,
  rtpData,
}: WithdrawalInputProps) => {
  const [amount, setAmount] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [inputFocused, setInputFocused] = useState<boolean>(false);

  const withdrawalAmountValidated = useMemo(() => {
    const parsedAmount = parseFloat(amount);
    if (amount.length === 0) {
      setError('');
      return true;
    }
    if (isNaN(parsedAmount)) {
      setError(_m('Invalid amount'));
      return false;
    }
    if (parsedAmount < 2) {
      setError(_m('Minimum withdrawal amount is $2.00'));
      return false;
    }

    if (parsedAmount > withdrawLimit) {
      setError(_m('Withdrawal amount exceeds limit'));
      return false;
    }
    setError('');
    return true;
  }, [amount]);

  const handleChangeText = (value: string) => {
    setAmount(value);
  };

  // This is used to update the value on the bank withdrawal screen
  // separate from the onChangeText function to avoid re-rendering the bank withdrawal screen on every keystroke
  const handleOnBlur = () => {
    setInputFocused(false);
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || amount.length === 0) {
      onChangeAmount?.('');
      return;
    }

    if (withdrawalAmountValidated) {
      setAmount(parsedAmount.toFixed(2));
      onChangeAmount?.(parsedAmount.toFixed(2));
      setError('');
    } else {
      setAmount(parsedAmount.toFixed(2));
      onChangeAmount?.('');
    }
  };

  const styles = stylesFn(inputFocused, withdrawalAmountValidated, amount);

  return (
    <View style={styles.withdrawalInputContainer}>
      <SkillzTextInput
        label={WITHDRAWAL_INPUT_LABEL}
        inputStyle={styles.inputStyle}
        containerStyle={styles.inputContainerStyle}
        labelStyle={styles.labelStyle}
        focusColor={withdrawalAmountValidated ? '#007BFF' : '#FF0000'}
        placeholderTextColor='#666'
        placeholder={WITHDRAWAL_INPUT_PLACEHOLDER}
        onChangeText={handleChangeText}
        value={amount}
        keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
        autoCapitalize={Platform.OS === 'android' ? 'none' : undefined}
        onBlur={handleOnBlur}
        returnKeyType='done'
        onFocus={() => {
          setInputFocused(true);
        }}
        allowFontScaling={false}
        labelTextProps={{allowFontScaling: false}}
      />
      <Text style={{color: 'red', marginBottom: 5}}>{error || ' '}</Text>
      <RTPBanner
        rtpData={rtpData}
        rtpEnabled={rtpEnabled || false}
        rtpEligible={rtpEligible || false}
        withdrawalAmount={parseFloat(amount)}
      />
    </View>
  );
};

export default WithdrawalInput;

const stylesFn = (inputFocused: boolean, withdrawalAmountValidated: boolean, amount: string) =>
  StyleSheet.create({
    withdrawalInputContainer: {},
    inputContainerStyle: {
      width: '100%',
      borderBottomWidth: 1,
      borderRightWidth: 0,
      borderLeftWidth: 0,
      borderTopWidth: 0,
      borderRadius: 0,
    },
    inputStyle: {
      left: 0,
    },
    labelStyle: {
      left: 0,
      bottom: 10,
      fontSize: inputFocused || amount ? 12 : 17,
      ...fontRegular,
      color: withdrawalAmountValidated ? '#007BFF' : '#FF0000',
      lineHeight: 17,
    },
  });
